/**
 * Brick Armory — one-shot scroll reveal (progressive enhancement).
 *
 * Content is visible by default. This script only hides `.reveal` elements
 * that start BELOW the viewport and fades them in once, the first time they
 * intersect. Anything above/inside the viewport, in the theme editor, with
 * reduced motion, or without IntersectionObserver is left untouched.
 */
(() => {
  const SELECTOR = '.reveal';
  const PENDING = 'reveal--pending';
  const IN = 'reveal--in';
  const STAGGER_MS = 90;
  const SCROLL_CHECK_MS = 500;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  if (!('IntersectionObserver' in window) || reducedMotion.matches || designMode) return;

  const show = (el) => {
    if (!el.classList.contains(PENDING)) return;
    el.classList.add(IN);
    el.classList.remove(PENDING);
  };

  const showAll = () => document.querySelectorAll(`.${PENDING}`).forEach(show);

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries.filter((e) => e.isIntersecting || e.boundingClientRect.top < 0);
      visible.forEach((entry, i) => {
        const el = entry.target;
        el.style.setProperty('--reveal-delay', `${Math.min(i, 4) * STAGGER_MS}ms`);
        observer.unobserve(el);
        // Next frame so the pending state is painted before transitioning.
        requestAnimationFrame(() => show(el));
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
  );

  const init = (root = document) => {
    const viewportBottom = window.innerHeight;
    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (el.classList.contains(PENDING) || el.classList.contains(IN)) return;
      // Only hide what the visitor cannot see yet; never hide on-screen content.
      if (el.getBoundingClientRect().top < viewportBottom) return;
      el.classList.add(PENDING);
      observer.observe(el);
    });
  };

  init();

  // Safety nets: reveal everything before print, and if the tab is restored
  // from bfcache or motion preferences change mid-session.
  window.addEventListener('beforeprint', showAll);
  window.addEventListener('pageshow', (e) => e.persisted && showAll());
  reducedMotion.addEventListener?.('change', showAll);

  // If the observer never fires for an element (e.g. hidden parent), the
  // element would stay invisible — reveal stragglers once the user has
  // scrolled past them.
  let ticking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      setTimeout(() => {
        ticking = false;
        document.querySelectorAll(`.${PENDING}`).forEach((el) => {
          if (el.getBoundingClientRect().top < window.innerHeight) show(el);
        });
      }, SCROLL_CHECK_MS);
    },
    { passive: true }
  );
})();
